// Global Instructions Rule Applied!

import React, { forwardRef } from 'react'
import { InputNumber } from 'antd'
import { FieldValue } from '../types.ts'
import { createNumberInputFormatter, parseNumberInput } from '../utils/formatters.ts'

export interface ValuationInputProps {
  value: FieldValue
  onChange: (value: { preMoneyValue: number | null; postMoneyValue: number | null }) => void
  onKeyDown?: (e: React.KeyboardEvent) => void
  currencySymbol?: string
  min?: number
  max?: number
  step?: number
}

export const ValuationInput = forwardRef<HTMLInputElement, ValuationInputProps>(
  ({ value, onChange, onKeyDown, currencySymbol = '$', min, max, step = 0.01 }, ref) => {
    let valuationData: { preMoneyValue: number | null; postMoneyValue: number | null } = {
      preMoneyValue: null,
      postMoneyValue: null
    }
    if (typeof value === 'number') {
      valuationData = { preMoneyValue: value, postMoneyValue: null }
    } else if (value && typeof value === 'object' && 'preMoneyValue' in value) {
      valuationData = value as { preMoneyValue: number | null; postMoneyValue: number | null }
    }

    const numberFormatter = createNumberInputFormatter()
    const numberParser = parseNumberInput

    // Convert number values to strings for stringMode
    const preMoneyStringValue =
      valuationData.preMoneyValue !== null && valuationData.preMoneyValue !== undefined
        ? String(valuationData.preMoneyValue)
        : null
    const postMoneyStringValue =
      valuationData.postMoneyValue !== null && valuationData.postMoneyValue !== undefined
        ? String(valuationData.postMoneyValue)
        : null

    // Convert string values back to numbers in onChange handlers
    const handlePreMoneyChange = (val: string | null) => {
      let preMoneyValue: number | null = null
      if (val !== null && val !== '') {
        const parsed = parseFloat(val)
        preMoneyValue = isNaN(parsed) ? null : parsed
      }
      const newData = { ...valuationData, preMoneyValue }
      onChange(newData)
    }

    const handlePostMoneyChange = (val: string | null) => {
      let postMoneyValue: number | null = null
      if (val !== null && val !== '') {
        const parsed = parseFloat(val)
        postMoneyValue = isNaN(parsed) ? null : parsed
      }
      const newData = { ...valuationData, postMoneyValue }
      onChange(newData)
    }

    return (
      <div className='flex flex-col' style={{ gap: '4px', width: '100%' }}>
        <InputNumber
          stringMode
          value={preMoneyStringValue}
          onChange={handlePreMoneyChange}
          formatter={numberFormatter}
          parser={numberParser}
          addonBefore={currencySymbol}
          addonAfter={<div className='text-xs w-6'>Pre</div>}
          size='small'
          style={{ width: '100%' }}
          step={step}
          min={String(min || 0)}
          autoFocus
          onKeyDown={onKeyDown}
        />
        <InputNumber
          stringMode
          value={postMoneyStringValue}
          onChange={handlePostMoneyChange}
          formatter={numberFormatter}
          parser={numberParser}
          addonBefore={currencySymbol}
          addonAfter={<div className='text-xs w-6'>Post</div>}
          size='small'
          style={{ width: '100%' }}
          step={step}
          min={String(min || 0)}
          onKeyDown={onKeyDown}
        />
      </div>
    )
  }
)

ValuationInput.displayName = 'ValuationInput'
